import styled from '@emotion/styled'
import type { MathflatApi } from '@mathflat/domain/@entities/Login/api'
import { addMonths, endOfToday } from 'date-fns'
import Cookies from 'js-cookie'
import { useEffect, useRef } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'

import { authApi } from '~/@common/api'
import { useStudentAppMediaQuery } from '~/@common/hooks/useMediaQuery'
import authService from '~/@common/services/auth.service'
import modalService from '~/@common/services/modal.service'
import { commonRepo } from '~/@common/services/repo.service'
import validationService from '~/@common/services/validation.service'
import { colors, typo } from '~/@common/styles'
import Button from '~/@common/ui/(Button)/Button/Button'
import CheckboxInputLabel from '~/@common/ui/checkbox/CheckboxInputLabel'
import { ValidationInput } from '~/@common/ui/ValidationInput'
import LogoutAfterPasswordChangeModal from './LogoutAfterPasswordChangeModal'

type Props = {
  isInitial?: boolean
}

const PasswordChangeModal = ({ isInitial = false }: Props) => {
  const ref = useRef<HTMLInputElement>(null)

  const {
    register,
    formState: { errors },
    handleSubmit,
    setError,
  } = useForm({
    defaultValues: {
      oldPassword: '',
      newPassword: '',
      newPasswordCheck: '',
    },
  })
  const { isMobile } = useStudentAppMediaQuery()

  const onSubmit: SubmitHandler<MathflatApi.Request.Password> = async (data) => {
    if (data.newPassword !== data.newPasswordCheck) {
      setError('newPasswordCheck', {
        message: '비밀번호가 일치하지 않습니다',
      })
      return
    }

    authApi
      .passwordChange(data)
      .then(() => {
        modalService.closeModal()
        modalService.openModal(
          <LogoutAfterPasswordChangeModal
            onConfirm={() => {
              authService.logout()
              modalService.closeModal()
            }}
          />,
          {
            modalName: '비밀번호 변경 후 로그아웃 모달',
            onAfterClose: () => {
              authService.logout()
              return true
            },
          },
        )
      })
      .catch((error) => {
        if (error.response.data.code === 'USER_WRONG_PASSWORD') {
          setError('oldPassword', {
            message: '비밀번호가 일치하지 않습니다',
          })
        }
      })
  }

  const handleDoNotShowAgainForThreeMonths = () => {
    const studentId = commonRepo.student?.id

    Cookies.set(`doNotShowAgainForThreeMonths-password_change-${studentId}`, 'true', {
      expires: addMonths(endOfToday(), 3),
    })
  }

  useEffect(() => {
    const checkBox = ref.current

    return () => {
      if (!isInitial || !checkBox?.checked) return

      handleDoNotShowAgainForThreeMonths()
    }
  }, [isInitial])

  return (
    <>
      <S.Container>
        <div className="description">
          초기 비밀번호는 123456 입니다.
          <br />
          영문 대/소문자, 숫자, 특수 문자 중 2가지를 조합하여 <br /> 6자 이상 길이로 만들어주세요.
          <br />
          <br />
          보안을 위해 로그인된 모든 기기에서 자동으로 로그아웃됩니다. <br />
          변경 후 새 비밀번호로 다시 로그인해 주세요.
        </div>
        <div className="form">
          <div className="form-item">
            <div className="form-item-title">현재 비밀번호</div>
            <ValidationInput
              className="form-item-input"
              type="password"
              placeholder="현재 비밀번호를 입력해주세요."
              {...register('oldPassword', {
                required: validationService.requiredMessage,
              })}
              errorMessage={errors.oldPassword?.message}
            />
          </div>
          <div className="form-item">
            <div className="form-item-title">새로운 비밀번호</div>
            <ValidationInput
              className="form-item-input"
              type="password"
              placeholder="새로운 비밀번호를 입력해주세요."
              {...register('newPassword', {
                required: validationService.requiredMessage,
                validate: validationService.password,
              })}
              errorMessage={errors.newPassword?.message}
            />
          </div>
          <div className="form-item">
            <div className="form-item-title">비밀번호 확인</div>
            <ValidationInput
              type="password"
              className="form-item-input"
              placeholder="다시 한 번 입력해주세요"
              errorMessage={errors.newPasswordCheck?.message}
              {...register('newPasswordCheck', {
                required: validationService.requiredMessage,
              })}
            />
          </div>
        </div>

        <div className="button-container">
          <Button
            theme="white"
            size="large"
            style={{ borderRadius: '8px', width: '100%' }}
            onClick={() => modalService.closeModal()}
          >
            취소
          </Button>
          <Button
            size="large"
            theme="primary"
            style={{ borderRadius: '8px', width: '100%' }}
            onClick={handleSubmit(onSubmit)}
          >
            변경 완료
          </Button>
        </div>
      </S.Container>
      {isInitial && (
        <S.Bottom>
          <div className="option">
            <CheckboxInputLabel ref={ref} className="option">
              3개월간 보지 않기
            </CheckboxInputLabel>
          </div>
        </S.Bottom>
      )}
    </>
  )
}

export default PasswordChangeModal

export const S = {
  Container: styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: clamp(267px, 90vw, 444px);
    box-sizing: border-box;
    height: 100%;
    padding: 0 30px 30px;

    .title {
      ${typo.body01};
      color: ${colors.gray.$900};
      font-weight: 700;
      margin-bottom: 20px;
    }

    .description {
      ${typo.caption01};
      width: 100%;
      background-color: ${colors.gray.$100};
      border-radius: 12px;
      padding: 14px 27px;
      color: ${colors.gray.$700};
      text-align: center;
      work-break: keep-all;
      display: flex;
      justify-content: center;
    }

    .form {
      display: flex;
      flex-direction: column;
      width: 100%;
      gap: 24px;
      margin: 20px 0 26px;

      .form-item {
        display: flex;
        flex-direction: column;
        width: 100%;
      }

      .form-item-title {
        ${typo.body02};
        margin-bottom: 14px;
        color: ${colors.gray.$900};
        font-weight: 700;
      }

      .form-item-input {
        width: 100%;
      }
    }

    .button-container {
      display: flex;
      width: 100%;
      gap: 10px;
    }

    .logout-button {
      ${typo.caption01};
      margin-top: 20px;
      color: ${colors.gray.$700};
      text-decoration: underline;
      cursor: pointer;
    }
  `,
  Bottom: styled.div`
    position: absolute;
    left: 0;
    bottom: -10px;
    width: 100%;
    display: flex;
    justify-content: space-between;
    transform: translateY(100%);

    .option {
      margin-right: auto;
      color: ${colors.mono.white};
    }
  `,
}
